import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Flame, ShieldCheck, Users, Wrench, Award, ArrowUpRight } from "lucide-react";
import { Counter } from "../components/Counter";
import { COMPANY, STATS, ABOUT_IMAGE } from "../data/site";

const About = () => {
    return (
        <>
            <PageHero
                eyebrow="About VIL"
                title="Engineering safer industries, one site at a time."
                subtitle="A licensed fire safety agency built on technical precision, compliance discipline, and long-term client trust."
            />

            <section className="section" data-testid="about-overview-section">
                <div className="container-vil grid lg:grid-cols-2 gap-14 items-center">
                    <div className="relative">
                        <div className="overflow-hidden corner-accent">
                            <img
                                src={ABOUT_IMAGE}
                                alt="VIL engineering team"
                                className="w-full h-[540px] object-cover"
                            />
                        </div>
                    </div>
                    <div>
                        <span className="eyebrow">Company Overview</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                            Turnkey fire safety,{" "}
                            <span className="text-[#E53935]">delivered end-to-end.</span>
                        </h2>
                        <p className="text-slate-600 mt-6 leading-relaxed">
                            VIL Engineering is an experienced fire safety engineering firm
                            based in Palghar, Maharashtra, serving industrial and commercial
                            clients across the MMR & MIDC corridor. From concept design and
                            hydraulic calculations to installation, testing, commissioning,
                            and AMC — we cover the complete lifecycle of every fire
                            protection system we deploy.
                        </p>
                        <p className="text-slate-600 mt-4 leading-relaxed">
                            We work directly with builders, EPC contractors, plant heads,
                            facility managers, and government bodies — ensuring every
                            installation passes statutory NOC, complies with NBC & IS norms,
                            and stands up to real emergencies.
                        </p>

                        <div className="grid grid-cols-2 gap-6 mt-10 pt-10 border-t border-slate-200">
                            {STATS.slice(0, 4).map((s) => (
                                <div key={s.label}>
                                    <div className="font-display font-black text-[#E53935] text-4xl">
                                        <Counter to={s.value} suffix={s.suffix} />
                                    </div>
                                    <div className="text-xs text-slate-600 font-mono uppercase tracking-widest mt-2">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section
                className="section relative overflow-hidden"
                style={{ background: "#f7f7f8" }}
                data-testid="mission-vision"
            >
                <div className="red-glow -right-40 top-10" />
                <div className="container-vil grid md:grid-cols-2 gap-6 relative z-10">
                    <PillarCard
                        icon={<Target className="w-6 h-6 text-[#E53935]" />}
                        title="Our Mission"
                        body="To protect lives, assets, and operations by delivering meticulously engineered fire safety systems backed by uncompromised compliance, skilled execution, and lifelong service support."
                    />
                    <PillarCard
                        icon={<Eye className="w-6 h-6 text-[#E53935]" />}
                        title="Our Vision"
                        body="To be Maharashtra's most trusted fire safety partner — the engineering team that industrial leaders, hospitals, and public institutions call first when fire safety is non-negotiable."
                    />
                </div>
            </section>

            <section className="section" data-testid="strengths-section">
                <div className="container-vil">
                    <div className="max-w-3xl">
                        <span className="eyebrow">Our Strengths</span>
                        <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 leading-tight">
                            Core capabilities that define our work.
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
                        {[
                            {
                                icon: <ShieldCheck className="w-6 h-6 text-[#E53935]" />,
                                title: "Licensed Operations",
                                body: "Licensed agency authorized for Fire Fighting, Sprinkler, Detection & Suppression installations.",
                            },
                            {
                                icon: <Flame className="w-6 h-6 text-[#E53935]" />,
                                title: "Fire Safety Commitment",
                                body: "Every project is delivered with rigorous adherence to NBC, IS, and local fire authority norms.",
                            },
                            {
                                icon: <Wrench className="w-6 h-6 text-[#E53935]" />,
                                title: "Technical Expertise",
                                body: "Hydraulic design, pump sizing, addressable wiring schemes — handled by qualified engineers.",
                            },
                            {
                                icon: <Award className="w-6 h-6 text-[#E53935]" />,
                                title: "Engineering Excellence",
                                body: "We pair OEM-grade components with detail-obsessed installation practices.",
                            },
                            {
                                icon: <Users className="w-6 h-6 text-[#E53935]" />,
                                title: "Skilled Team",
                                body: "Field engineers and project managers trained for industrial-scale executions.",
                            },
                            {
                                icon: <ArrowUpRight className="w-6 h-6 text-[#E53935]" />,
                                title: "Industry Diversity",
                                body: "Active projects across pharma, healthcare, education, govt., dairy, and manufacturing.",
                            },
                        ].map((s, i) => (
                            <PillarCard
                                key={s.title}
                                icon={s.icon}
                                title={s.title}
                                body={s.body}
                                idx={i}
                            />
                        ))}
                    </div>
                </div>
            </section>

            <section
                className="section relative overflow-hidden"
                style={{ background: "#f7f7f8" }}
                data-testid="team-section"
            >
                <div className="container-vil text-center">
                    <span className="eyebrow">Get Started</span>
                    <h2 className="font-display font-black text-slate-900 text-4xl md:text-5xl mt-4 max-w-3xl mx-auto leading-tight">
                        Partner with VIL for your next fire safety project.
                    </h2>
                    <p className="text-slate-600 mt-6 max-w-xl mx-auto leading-relaxed">
                        From initial site survey to long-term AMC — speak to our
                        technical team and get a tailored proposal.
                    </p>
                    <div className="mt-10">
                        <Link to="/contact" className="btn-primary" data-testid="about-cta-contact">
                            Schedule a Consultation <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
};

export const PageHero = ({ eyebrow, title, subtitle }) => (
    <section
        className="relative pt-20 md:pt-28 pb-20 overflow-hidden bg-white border-b border-slate-100"
        data-testid="page-hero"
    >
        <div className="red-glow -left-60 -top-20" />
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-40 pointer-events-none"
             style={{ background: "linear-gradient(135deg, transparent 0%, #fff1f0 100%)" }} />
        <div className="container-vil relative z-10">
            <span className="eyebrow">{eyebrow}</span>
            <h1 className="font-display font-black text-slate-900 mt-4 leading-[1.05]" style={{ fontSize: "clamp(2.2rem, 5vw, 4.5rem)" }}>
                {title}
            </h1>
            {subtitle && (
                <p className="text-slate-600 mt-6 max-w-2xl text-lg leading-relaxed">
                    {subtitle}
                </p>
            )}
        </div>
    </section>
);

const PillarCard = ({ icon, title, body, idx }) => (
    <div className="glass p-8 corner-accent relative card-lift" data-testid={`pillar-${title.replace(/\s/g, "-").toLowerCase()}`}>
        <div className="flex items-center gap-4 mb-5">
            <div className="w-12 h-12 bg-[#E53935]/10 border border-[#E53935]/30 flex items-center justify-center">
                {icon}
            </div>
            {idx !== undefined && (
                <span className="text-xs font-mono text-slate-600">
                    /{String(idx + 1).padStart(2, "0")}
                </span>
            )}
        </div>
        <h3 className="font-display font-bold text-slate-900 text-2xl mb-3">{title}</h3>
        <p className="text-slate-600 leading-relaxed">{body}</p>
    </div>
);

export default About;
